import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardRoute } from '../services/api';
import api from '../services/api';

/* ── Toast ─────────────────────────────────────────────────────────── */
const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'error' ? '#fef2f2' : '#eaf3de',
    color:      type === 'error' ? '#dc2626'  : '#3b6d11',
    border: `0.5px solid ${type === 'error' ? '#fca5a5' : '#97c459'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s', pointerEvents: 'none',
  }}>
    {message}
  </div>
);

/* ── Field wrapper ──────────────────────────────────────────────────── */
const Field = ({ label, required, hint, children }) => (
  <div className="field" style={{ marginBottom: 0 }}>
    <label className="t-label">
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {hint && <div className="t-meta" style={{ marginTop: 3 }}>{hint}</div>}
  </div>
);

/* ── Main ───────────────────────────────────────────────────────────── */
const Profile = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, updateUser, markProfileCompleted } = useAuth();

  /* Is this a first-time-login setup flow? */
  const isFirstLogin = location.state?.firstLogin === true || !user?.profileCompleted;

  /* ── User fields (PUT /api/profile) ── */
  const [fullName, setFullName] = useState('');
  const [email,    setEmail]    = useState('');

  /* ── Patient / SELF fields (PUT /api/patients/:id) ── */
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [age,         setAge]         = useState('');
  const [gender,      setGender]      = useState('');
  const [bloodGroup,  setBloodGroup]  = useState('');

  /* ── UI state ── */
  const [patientId, setPatientId] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState({ visible: false, message: '', type: 'success' });

  /* ── Load profile from backend ── */
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const [meRes, patientsRes] = await Promise.all([
          api.get('/me'),
          api.get('/patients/my').catch(() => ({ data: [] })),
        ]);

        const me = meRes.data;
        setFullName(me.fullName || '');
        setEmail(me.email || '');
        updateUser({
          name: me.fullName,
          email: me.email,
          profileCompleted: me.profileCompleted,
        });

        const selfPatient = (patientsRes.data || []).find(p => p.isSelf);
        if (selfPatient) {
          setPatientId(selfPatient.id);
          setDateOfBirth(selfPatient.dateOfBirth || '');
          setAge(selfPatient.age != null ? String(selfPatient.age) : '');
          setGender(selfPatient.gender || '');
          setBloodGroup(selfPatient.bloodGroup || '');
        }
      } catch {
        /* fall back to JWT data silently */
        setFullName(user?.name || '');
        setEmail(user?.email || '');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2500);
  };

  /* ── Save ── */
  const handleSave = async () => {
    if (!fullName.trim()) { showToast('Full name is required', 'error'); return; }
    if (!gender)          { showToast('Gender is required', 'error'); return; }

    setSaving(true);
    try {
      /* 1. Update user profile (name + email) */
      const { data: updatedUser } = await api.put('/profile', {
        fullName: fullName.trim(),
        email:    email.trim() || undefined,
      });

      updateUser({
        name:             updatedUser.fullName,
        email:            updatedUser.email,
        profileCompleted: updatedUser.profileCompleted,
      });
      if (updatedUser.profileCompleted) markProfileCompleted();

      /* 2. Update SELF patient record (upsert) */
      const patientPayload = {
        fullName:    fullName.trim(),
        email:       email.trim() || undefined,
        phone:       user?.phone,
        dateOfBirth: dateOfBirth || undefined,
        age:         age ? Number(age) : undefined,
        gender,
        bloodGroup:  bloodGroup || undefined,
        isSelf:      true,
        relation:    'SELF',
      };

      if (patientId) {
        await api.put(`/patients/${patientId}`, patientPayload);
      } else {
        const { data: newPatient } = await api.post('/patients', patientPayload);
        setPatientId(newPatient.id);
      }

      showToast(isFirstLogin ? 'Profile set up successfully! 🎉' : 'Profile updated successfully');

      if (isFirstLogin) {
        setTimeout(() => navigate(getDashboardRoute(user?.role)), 1200);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to save profile';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── Role display ── */
  const roleConfig = {
    ADMIN:        { label: 'Admin',        color: '#4f46e5', bg: '#eef2ff' },
    super_admin:  { label: 'Super Admin',  color: '#4f46e5', bg: '#eef2ff' },
    system_admin: { label: 'System Admin', color: '#2563eb', bg: '#eff6ff' },
    tenant_admin: { label: 'Tenant Admin', color: '#0891b2', bg: '#ecfeff' },
    staff:        { label: 'Staff',        color: '#059669', bg: '#ecfdf5' },
    DOCTOR:       { label: 'Doctor',       color: '#7c3aed', bg: '#f5f3ff' },
    doctor:       { label: 'Doctor',       color: '#7c3aed', bg: '#f5f3ff' },
    PATIENT:      { label: 'Patient',      color: '#d97706', bg: '#fffbeb' },
    end_user:     { label: 'Patient',      color: '#d97706', bg: '#fffbeb' },
  };
  const rc = roleConfig[user?.role] || { label: user?.role, color: '#6b7280', bg: '#f9fafb' };

  const initials = fullName
    ? fullName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : (user?.phone || '??').slice(0, 2).toUpperCase();

  /* ── Render ── */
  return (
    <div className="page">
      <main className="page-main">

        {/* ── Header ── */}
        {!isFirstLogin && (
          <div className="breadcrumb">
            <button className="t-link" onClick={() => navigate('/')}>Home</button>
            <span>›</span>
            <span className="bc-current">Profile</span>
          </div>
        )}

        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          {isFirstLogin ? (
            <>
              <h1 className="t-h1" style={{ marginBottom: 4 }}>👋 Welcome! Let's set up your profile</h1>
              <p className="t-body-sm">Complete your profile to get the best experience on ArogyaOne.</p>
            </>
          ) : (
            <>
              <h1 className="t-h1" style={{ marginBottom: 4 }}>👤 My Profile</h1>
              <p className="t-body-sm">View and update your personal information</p>
            </>
          )}
        </div>

        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
            Loading profile…
          </div>
        ) : (
          <>
            {/* ── Avatar + role badge ── */}
            <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${rc.color}dd, ${rc.color}88)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: 1,
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 17, color: '#1e1b4b' }}>
                  {fullName || user?.phone}
                </div>
                <div className="t-meta" style={{ marginTop: 2 }}>{user?.phone}</div>
                <div className="badge" style={{ marginTop: 6, background: rc.bg, border: `1px solid ${rc.color}33`, color: rc.color }}>
                  {rc.label}
                </div>
              </div>
            </div>

            {/* ── Account fields ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 className="t-h3" style={{ marginBottom: 20 }}>Account information</h3>

              <div className="grid-2col" style={{ marginBottom: 16 }}>
                <Field label="Full name" required>
                  <input
                    className="input"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Your full name"
                  />
                </Field>
                <Field label="Email address">
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </Field>
              </div>

              <Field label="Phone number" hint="Phone number cannot be changed.">
                <input
                  className="input"
                  value={user?.phone || ''}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </Field>
            </div>

            {/* ── Health / patient fields ── */}
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 className="t-h3" style={{ marginBottom: 4 }}>Health information</h3>
              <p className="t-body-sm" style={{ marginBottom: 20, color: '#6b7280' }}>
                Used for medical records and appointments
              </p>

              <div className="grid-2col" style={{ marginBottom: 16 }}>
                <Field label="Gender" required>
                  <select
                    className="input"
                    value={gender}
                    onChange={e => setGender(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </Field>
                <Field label="Blood group">
                  <select
                    className="input"
                    value={bloodGroup}
                    onChange={e => setBloodGroup(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">Select blood group</option>
                    {['A+', 'A−', 'B+', 'B−', 'AB+', 'AB−', 'O+', 'O−'].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid-2col" style={{ marginBottom: 0 }}>
                <Field label="Date of birth">
                  <input
                    className="input"
                    type="date"
                    value={dateOfBirth}
                    onChange={e => {
                      setDateOfBirth(e.target.value);
                      if (e.target.value) {
                        const today = new Date();
                        const dob   = new Date(e.target.value);
                        const years = today.getFullYear() - dob.getFullYear() -
                          (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                        setAge(String(years));
                      }
                    }}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Field>
                <Field label="Age" hint="Auto-filled from date of birth">
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max="150"
                    value={age}
                    onChange={e => setAge(e.target.value)}
                    placeholder="e.g. 30"
                  />
                </Field>
              </div>
            </div>

            {/* ── Save button ── */}
            <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btn-primary"
                style={{ maxWidth: 220 }}
                disabled={saving || !fullName.trim() || !gender}
                onClick={handleSave}
              >
                {saving
                  ? 'Saving…'
                  : isFirstLogin
                    ? 'Complete profile & continue →'
                    : 'Save changes'}
              </button>

              {!isFirstLogin && (
                <button
                  className="btn-ghost"
                  onClick={() => navigate('/')}
                  style={{ fontSize: 13 }}
                >
                  Cancel
                </button>
              )}
            </div>

            {/* ── Account details (read-only, non-first-login) ── */}
            {!isFirstLogin && (
              <div className="card" style={{ marginTop: 16 }}>
                <h3 className="t-h3" style={{ marginBottom: 20 }}>Account details</h3>
                <div style={{ padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>Role</div>
                  <div className="t-meta">{rc.label}</div>
                </div>
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13.5, color: '#1e1b4b', marginBottom: 2 }}>Member since</div>
                  <div className="t-meta">
                    {user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                      : 'N/A'}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </main>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </div>
  );
};

export default Profile;
